// slices/courseSession/courseSessionRules.ts
//
// The order rules of a course session, as pure functions over the server's
// payloads (ora-workspace/docs/courses/FRONTEND-INTEGRATION.md).
//
// Everything is addressed by id and read off the *latest* payload, never a
// position captured at Start. Where the server already says something — the
// course's `resume` pointer — the rule reads it rather than re-deriving it.

import type {
  CourseDetail,
  CourseModuleSummary,
  CourseStepKind,
  CourseStepStatus,
  CourseStepSummary,
} from '../../types/course';
import { courseStepLabel } from '../../types/course';
import type { ChatAnchorIcon } from '../../types/chatEnvelope';

/** The index of the first step that is not COMPLETED; `steps.length` if all are. */
export const firstIncompleteStepIndex = (
  steps: CourseStepSummary[]
): number => {
  const index = steps.findIndex((step) => step.progress.status !== 'COMPLETED');
  return index === -1 ? steps.length : index;
};

/**
 * Where a module opens when the server's `resume` does not name a step in it:
 * its first non-COMPLETED step, or its last step when every step is done.
 */
export const entryStepId = (steps: CourseStepSummary[]): string | null => {
  if (steps.length === 0) return null;
  const index = Math.min(firstIncompleteStepIndex(steps), steps.length - 1);
  return steps[index].id;
};

/**
 * Whether the rail lets the student open this step: any step of the module,
 * in any order (the server accepts work out of order). Continue still walks
 * the steps in the author's order.
 */
export const isStepReachable = (
  steps: CourseStepSummary[],
  stepId: string
): boolean => steps.some((step) => step.id === stepId);

/**
 * The step Continue goes to from `currentId`: the first non-COMPLETED step
 * after it in the server's order. Null when there is none, which is what sends
 * the session to the end screen.
 */
export const nextStepId = (
  steps: CourseStepSummary[],
  currentId: string | null
): string | null => {
  const currentIndex = steps.findIndex((step) => step.id === currentId);

  const next = steps
    .slice(currentIndex + 1)
    .find((step) => step.progress.status !== 'COMPLETED');

  return next?.id ?? null;
};

/** A module's status as its steps say it. */
export const moduleStatusFromSteps = (
  steps: CourseStepSummary[]
): CourseStepStatus => {
  if (
    steps.length > 0 &&
    steps.every((step) => step.progress.status === 'COMPLETED')
  ) {
    return 'COMPLETED';
  }

  return steps.some((step) => step.progress.status !== 'NOT_STARTED')
    ? 'IN_PROGRESS'
    : 'NOT_STARTED';
};

/**
 * The module Start (or Continue Ahead) opens: the one the server's `resume`
 * pointer names. Null when the course is complete.
 */
export const resumeModule = (
  course: CourseDetail | null
): CourseModuleSummary | null => {
  const moduleId = course?.resume?.moduleId;
  if (!moduleId) return null;
  return course?.modules.find((module) => module.id === moduleId) ?? null;
};

/** The step the server's `resume` pointer names, when the course lists it. */
export const resumeStep = (
  course: CourseDetail | null
): CourseStepSummary | null => {
  const stepId = course?.resume?.stepId;
  if (!stepId) return null;
  return resumeModule(course)?.steps.find((step) => step.id === stepId) ?? null;
};

/**
 * What the companion calls the course on the start screen: the step the Start
 * button opens, by its label, or the course itself once nothing is left.
 */
export const courseStartAnchorLabel = (
  course: CourseDetail | null
): string | undefined => {
  const step = resumeStep(course);
  if (step) return courseStepLabel(step);
  return course?.title || undefined;
};

/** The content-type icon for the step the Start button opens; none when done. */
export const courseStartAnchorIcon = (
  course: CourseDetail | null
): ChatAnchorIcon | undefined => {
  const step = resumeStep(course);
  if (!step) return undefined;
  return COURSE_STEP_KIND_ICON[step.kind];
};

const COURSE_STEP_KIND_ICON: Record<CourseStepKind, ChatAnchorIcon> = {
  ARTICLE: 'reading',
  VIDEO: 'video',
  QBANK: 'question',
  FLASHCARDS: 'flashcard',
  ASSESSMENT: 'question',
  NARRATION: 'reading',
  CONVERSATION: 'question',
};

export const isCourseComplete = (course: CourseDetail | null): boolean =>
  course !== null && course.resume === null;

/** How many items of each kind a module holds, summed over its steps. */
export const moduleContentCounts = (
  module: Pick<CourseModuleSummary, 'steps'>
): Record<CourseStepKind, number> => {
  const counts: Record<CourseStepKind, number> = {
    QBANK: 0,
    FLASHCARDS: 0,
    VIDEO: 0,
    ARTICLE: 0,
    ASSESSMENT: 0,
    // A narration has no items; the key is kept, and nothing displays it.
    NARRATION: 0,
    // A practice conversation has no items either.
    CONVERSATION: 0,
  };

  for (const step of module.steps) counts[step.kind] += step.itemCount;

  return counts;
};

/** A module is complete when every one of its steps is. */
/** Items per kind in the module's COMPLETED steps: the "done" of a count. */
export const moduleCompletedContentCounts = (
  module: Pick<CourseModuleSummary, 'steps'>
): Record<CourseStepKind, number> =>
  moduleContentCounts({
    steps: module.steps.filter((step) => step.progress.status === 'COMPLETED'),
  });

export const isModuleComplete = (
  module: Pick<CourseModuleSummary, 'stepCount' | 'completedStepCount'>
): boolean =>
  module.stepCount > 0 && module.completedStepCount >= module.stepCount;
